import { useMemo, useState, useContext } from "react";
import { useTable } from "react-table";
import {
  convertToTilteCase,
  defaultDateDisplay,
  truncateString,
} from "../../../helpers";
import primaryComponents from "../../primaryComponents";
import secondaryComponents from "..";
import globalStyles from "../../../assets/styles/base/globalPage.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { pathConstants } from "../../../routes/pathContants";
import { useDeleteStockRemovalRequest } from "../../../hooks/api/mutations/useInventory";
import { ToastContext } from "../../../hooks/context";

const useStockRemovalsTable = ({
  stockRemovals = [],
  refetch = () => {},
  onContinueDraft = () => {},
}) => {
  const navigate = useNavigate();
  const triggerToast = useContext(ToastContext);
  const [previewImage, setPreviewImage] = useState(null);
  const [requestToDelete, setRequestToDelete] = useState(null);

  const { mutate: deleteRequest, isLoading: isDeleting } =
    useDeleteStockRemovalRequest();

  const handleDelete = (id) => {
    deleteRequest(id, {
      onSuccess: () => {
        triggerToast("Stock removal request deleted successfully", "success");
        setRequestToDelete(null);
        refetch();
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Date/Time",
        accessor: "submitted_at",
        Cell: ({ cell: { value }, row }) =>
          defaultDateDisplay(value || row?.original?.created_at),
      },
      {
        Header: "Request ID",
        accessor: "request_id",
        Cell: ({ cell: { value }, row }) => {
          const id = row?.original?.id;
          return (
            <Link
              to={pathConstants.INVENTORY_STOCK_REMOVALS_REVIEW({ id })}
              className="smallest-text-size purple--text font-weight-normal"
              style={{
                fontSize: "12px",
                fontWeight: 400,
                textDecoration: "underline",
              }}
            >
              #{value || id}
            </Link>
          );
        },
      },
      {
        Header: "Store",
        accessor: "store",
        Cell: ({ cell: { value } }) => <span>{value?.name || "-"}</span>,
      },
      {
        Header: "Products",
        accessor: "items",
        Cell: ({ cell: { value } }) => {
          if (!value || value.length === 0) return "-";
          const firstItem = value[0];
          const productName = firstItem?.product_name || "Product";
          const moreCount = value.length - 1;

          return (
            <secondaryComponents.ToolTipV2
              tipText={value
                ?.map(
                  (i) =>
                    `${i?.product_name || "Product"} (Qty: ${i?.quantity || 1})`,
                )
                .join(", ")}
            >
              <span>
                {truncateString(productName, 30)}
                {moreCount > 0 ? (
                  <span
                    style={{
                      color: "#7647EE",
                      marginLeft: "4px",
                      fontWeight: 600,
                    }}
                  >
                    (+{moreCount})
                  </span>
                ) : null}
              </span>
            </secondaryComponents.ToolTipV2>
          );
        },
      },
      {
        Header: "Quantity",
        accessor: (row) => {
          return (
            row?.items?.reduce(
              (sum, item) => sum + (Number(item?.quantity) || 0),
              0,
            ) || 0
          );
        },
        id: "total_quantity",
        Cell: ({ cell: { value } }) => <span>{value}</span>,
      },
      {
        Header: "Reason",
        accessor: (row) => row?.items?.[0]?.reason,
        id: "reason",
        Cell: ({ row }) => {
          const item = row?.original?.items?.[0];
          const reason =
            item?.reason_display ||
            (item?.reason
              ? convertToTilteCase(item.reason.replace(/_/g, " "))
              : "N/A");
          return <span>{reason}</span>;
        },
      },
      {
        Header: "Evidence",
        accessor: (row) =>
          row?.items?.flatMap((i) => i?.images || []).filter(Boolean) || [],
        id: "images",
        Cell: ({ cell: { value } }) => {
          if (Array.isArray(value) && value.length > 0) {
            return (
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                {value.slice(0, 2).map((imgUrl, idx) => (
                  <img
                    key={idx}
                    src={imgUrl}
                    alt="evidence"
                    style={{
                      width: "32px",
                      height: "32px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      cursor: "pointer",
                      border: "1px solid #E2E8F0",
                    }}
                    onClick={() => setPreviewImage(imgUrl)}
                  />
                ))}
                {value.length > 2 && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#7647EE",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                    onClick={() => setPreviewImage(value[0])}
                  >
                    +{value.length - 2}
                  </span>
                )}
              </div>
            );
          }
          return "-";
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => {
          const rawStatus = (value || "REVIEW").toUpperCase();
          const isApproved = rawStatus === "APPROVED";
          const isReview =
            rawStatus === "REVIEW" ||
            rawStatus === "IN_REVIEW" ||
            rawStatus === "PENDING";
          const isDraft = rawStatus === "DRAFT";
          const isRejected = rawStatus === "REJECTED";

          const statusClass = isApproved
            ? globalStyles["statusBlock--success"]
            : isReview
              ? globalStyles["statusBlock--pending"]
              : isDraft
                ? globalStyles["statusBlock--purple"]
                : isRejected
                  ? globalStyles["statusBlock--failed"]
                  : globalStyles.statusBlock;

          const displayText = isApproved
            ? "Approved"
            : isReview
              ? "In Review"
              : isDraft
                ? "Draft"
                : isRejected
                  ? "Rejected"
                  : rawStatus;

          return (
            <span
              className={`${globalStyles.statusBlock} ${statusClass}`}
              style={{
                whiteSpace: "nowrap",
                fontSize: "11px",
              }}
            >
              {displayText}
            </span>
          );
        },
      },
      {
        Header: "Initiator",
        accessor: "created_by",
        Cell: ({ cell: { value } }) => <span>{value?.first_name || "-"}</span>,
      },
      {
        Header: "Expiry Date",
        accessor: (row) => row?.items?.find((i) => i?.expiry_date)?.expiry_date,
        id: "expiry_date",
        Cell: ({ cell: { value } }) => defaultDateDisplay(value),
      },
      {
        Header: () => (
          <div style={{ textAlign: "left", width: "100%" }}>Actions</div>
        ),
        accessor: "actions",
        Cell: ({ row }) => {
          const id = row?.original?.id;
          const isDraft = row?.original?.status === "DRAFT";

          return (
            <div className="d-flex align-items-center justify-content-start w-100">
              {isDraft ? (
                <primaryComponents.Button
                  classNames="btn btn--outline-green smallBtn"
                  onClick={() => onContinueDraft(row?.original)}
                >
                  Continue Draft
                </primaryComponents.Button>
              ) : (
                <primaryComponents.Button
                  classNames="btn btn--primary smallBtn"
                  onClick={() =>
                    navigate(
                      pathConstants.INVENTORY_STOCK_REMOVALS_REVIEW({ id }),
                    )
                  }
                >
                  Review
                </primaryComponents.Button>
              )}
            </div>
          );
        },
      },
    ],
    [navigate, onContinueDraft],
  );

  const instance = useTable({ columns, data: stockRemovals });

  return {
    instance,
    previewImage,
    setPreviewImage,
    requestToDelete,
    setRequestToDelete,
    handleDelete,
    isDeleting,
  };
};

export default useStockRemovalsTable;
